import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  User,
  Loader2,
  CheckCircle,
  AlertCircle,
  Calendar,
  RefreshCw,
  Phone,
  Briefcase,
  Mail,
  Server,
  Trash2,
  Star,
  TestTube,
} from 'lucide-react'
import { profileApi, type Profile } from '@/services/profile-api'
import {
  integrationsApi,
  emailIntegrationsApi,
  type CalendarConnection,
  type CalendarProvider,
  type EmailConnection,
  type EmailProvider,
  type SMTPConnectionData,
} from '@/services/api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Switch } from '@/components/ui/switch'

export default function AccountSettingsPage() {
  const queryClient = useQueryClient()
  const [fullName, setFullName] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(false)
  const [calendarConnections, setCalendarConnections] = useState<CalendarConnection[]>([])
  const [defaultCalendarProvider, setDefaultCalendarProvider] =
    useState<CalendarProvider | null>(null)
  const [calendarBusyProvider, setCalendarBusyProvider] =
    useState<CalendarProvider | null>(null)

  // Email connection state
  const [isLoadingEmails, setIsLoadingEmails] = useState(false)
  const [emailConnections, setEmailConnections] = useState<EmailConnection[]>([])
  const [defaultEmailConnectionId, setDefaultEmailConnectionId] = useState<string | null>(null)
  const [emailBusyProvider, setEmailBusyProvider] = useState<EmailProvider | null>(null)
  const [testingConnectionId, setTestingConnectionId] = useState<string | null>(null)

  // SMTP dialog state
  const [smtpDialogOpen, setSmtpDialogOpen] = useState(false)
  const [smtpForm, setSmtpForm] = useState<SMTPConnectionData>({
    email_address: '',
    display_name: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
  })
  const [isSavingSMTP, setIsSavingSMTP] = useState(false)

  const { data: profile, isLoading: isLoadingProfile } = useQuery<Profile>({
    queryKey: ['profile'],
    queryFn: profileApi.getMe,
  })

  const updateProfileMutation = useMutation({
    mutationFn: (data: Partial<Profile>) => profileApi.updateMe(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['profile-setup'] })
      setMessage({ type: 'success', text: 'Profile saved successfully' })
      setTimeout(() => setMessage(null), 3000)
    },
    onError: () => {
      setMessage({ type: 'error', text: 'Error saving profile' })
      setTimeout(() => setMessage(null), 3000)
    },
  })

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '')
      setJobTitle(profile.job_title || '')
      setPhone(profile.phone || '')
    }
  }, [profile])

  const refreshCalendarConnections = async () => {
    setIsLoadingCalendars(true)
    try {
      const data = await integrationsApi.listCalendarConnections()
      setCalendarConnections(data.connections || [])
      setDefaultCalendarProvider(data.default_provider)
    } catch (error) {
      console.error('Failed to load calendar connections', error)
    } finally {
      setIsLoadingCalendars(false)
    }
  }

  useEffect(() => {
    refreshCalendarConnections()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('calendar') === 'connected') {
      setMessage({ type: 'success', text: 'Calendar connected successfully' })
      refreshCalendarConnections()
      params.delete('calendar')
      const query = params.toString()
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`
      window.history.replaceState({}, '', nextUrl)
      setTimeout(() => setMessage(null), 3000)
    }
  }, [])

  const saveProfile = () => {
    updateProfileMutation.mutate({
      full_name: fullName.trim(),
      job_title: jobTitle.trim() || null,
      phone: phone.trim() || null,
    })
  }

  const connectCalendar = async (provider: CalendarProvider) => {
    setCalendarBusyProvider(provider)
    try {
      const payload = await integrationsApi.startCalendarOAuth(
        provider,
        window.location.pathname
      )
      window.location.href = payload.authorization_url
    } catch (error) {
      console.error('Failed to start OAuth', error)
      setMessage({ type: 'error', text: `Failed to connect ${provider} calendar` })
      setTimeout(() => setMessage(null), 3000)
      setCalendarBusyProvider(null)
    }
  }

  const setDefaultProvider = async (provider: CalendarProvider) => {
    setCalendarBusyProvider(provider)
    try {
      await integrationsApi.setDefaultCalendarProvider(provider)
      await refreshCalendarConnections()
      setMessage({ type: 'success', text: `Default calendar set to ${provider}` })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Failed to set default provider', error)
      setMessage({ type: 'error', text: `Failed to set default provider to ${provider}` })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setCalendarBusyProvider(null)
    }
  }

  const disconnectCalendar = async (
    connectionId: string,
    provider: CalendarProvider
  ) => {
    setCalendarBusyProvider(provider)
    try {
      await integrationsApi.disconnectCalendarConnection(connectionId)
      await refreshCalendarConnections()
      setMessage({ type: 'success', text: 'Calendar disconnected' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Failed to disconnect calendar', error)
      setMessage({ type: 'error', text: 'Failed to disconnect calendar' })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setCalendarBusyProvider(null)
    }
  }

  // Email connection handlers
  const refreshEmailConnections = async () => {
    setIsLoadingEmails(true)
    try {
      const data = await emailIntegrationsApi.listConnections()
      setEmailConnections(data.connections || [])
      setDefaultEmailConnectionId(data.default_connection_id)
    } catch (error) {
      console.error('Failed to load email connections', error)
    } finally {
      setIsLoadingEmails(false)
    }
  }

  useEffect(() => {
    refreshEmailConnections()
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('email') === 'connected') {
      setMessage({ type: 'success', text: 'Email account connected successfully' })
      refreshEmailConnections()
      params.delete('email')
      const query = params.toString()
      const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`
      window.history.replaceState({}, '', nextUrl)
      setTimeout(() => setMessage(null), 3000)
    }
  }, [])

  const connectEmail = async (provider: 'google' | 'microsoft') => {
    setEmailBusyProvider(provider)
    try {
      const payload = await emailIntegrationsApi.startOAuth(provider, window.location.pathname)
      window.location.href = payload.authorization_url
    } catch (error) {
      console.error('Failed to start email OAuth', error)
      setMessage({ type: 'error', text: `Failed to connect ${provider} email` })
      setTimeout(() => setMessage(null), 3000)
      setEmailBusyProvider(null)
    }
  }

  const saveSMTPConnection = async () => {
    setIsSavingSMTP(true)
    try {
      await emailIntegrationsApi.saveSMTPConnection(smtpForm)
      await refreshEmailConnections()
      setSmtpDialogOpen(false)
      setSmtpForm({
        email_address: '',
        display_name: '',
        smtp_host: '',
        smtp_port: 587,
        smtp_username: '',
        smtp_password: '',
        smtp_use_tls: true,
      })
      setMessage({ type: 'success', text: 'SMTP connection saved successfully' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Failed to save SMTP connection', error)
      setMessage({ type: 'error', text: 'Failed to save SMTP connection' })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setIsSavingSMTP(false)
    }
  }

  const setDefaultEmailConnection = async (connectionId: string) => {
    try {
      await emailIntegrationsApi.setDefaultConnection(connectionId)
      await refreshEmailConnections()
      setMessage({ type: 'success', text: 'Default email connection updated' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Failed to set default email connection', error)
      setMessage({ type: 'error', text: 'Failed to set default email connection' })
      setTimeout(() => setMessage(null), 3000)
    }
  }

  const testEmailConnection = async (connectionId: string) => {
    setTestingConnectionId(connectionId)
    try {
      const result = await emailIntegrationsApi.testConnection(connectionId)
      if (result.is_valid) {
        setMessage({ type: 'success', text: 'Email connection is healthy' })
      } else {
        setMessage({ type: 'error', text: 'Email connection test failed' })
      }
      await refreshEmailConnections()
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Failed to test email connection', error)
      setMessage({ type: 'error', text: 'Failed to test email connection' })
      setTimeout(() => setMessage(null), 3000)
    } finally {
      setTestingConnectionId(null)
    }
  }

  const disconnectEmail = async (connectionId: string) => {
    try {
      await emailIntegrationsApi.disconnectConnection(connectionId)
      await refreshEmailConnections()
      setMessage({ type: 'success', text: 'Email disconnected' })
      setTimeout(() => setMessage(null), 3000)
    } catch (error) {
      console.error('Failed to disconnect email', error)
      setMessage({ type: 'error', text: 'Failed to disconnect email' })
      setTimeout(() => setMessage(null), 3000)
    }
  }

  const getProviderLabel = (provider: EmailProvider) => {
    switch (provider) {
      case 'google':
        return 'Gmail'
      case 'microsoft':
        return 'Microsoft'
      case 'smtp':
        return 'SMTP'
    }
  }

  const getHealthBadge = (status: string) => {
    switch (status) {
      case 'healthy':
        return <Badge variant="default" className="bg-green-500">Healthy</Badge>
      case 'unhealthy':
        return <Badge variant="destructive">Unhealthy</Badge>
      default:
        return <Badge variant="secondary">Unknown</Badge>
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
        <p className="mt-1 text-muted-foreground">
          Manage your personal profile and calendar connections
        </p>
      </div>

      {message && (
        <Alert variant={message.type === 'error' ? 'destructive' : 'default'}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Your Profile
          </CardTitle>
          <CardDescription>
            Your personal information used in outreach emails and signatures
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingProfile ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  placeholder="Enter your full name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={updateProfileMutation.isPending}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="jobTitle" className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4 text-muted-foreground" />
                  Job Title
                </Label>
                <Input
                  id="jobTitle"
                  placeholder="e.g., Sales Manager, Account Executive"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  disabled={updateProfileMutation.isPending}
                />
                <p className="text-sm text-muted-foreground">
                  Used in email signatures and outreach personalization
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  Phone Number
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  disabled={updateProfileMutation.isPending}
                />
                <p className="text-sm text-muted-foreground">
                  Included in email signatures for direct contact
                </p>
              </div>

              <Button
                onClick={saveProfile}
                disabled={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Profile'
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Calendar Connections
          </CardTitle>
          <CardDescription>
            Connect your calendar to enable meeting scheduling in outreach
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={calendarBusyProvider !== null}
              onClick={() => connectCalendar('google')}
            >
              {calendarBusyProvider === 'google' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Connect Google
            </Button>
            <Button
              variant="outline"
              disabled={calendarBusyProvider !== null}
              onClick={() => connectCalendar('microsoft')}
            >
              {calendarBusyProvider === 'microsoft' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Connect Microsoft
            </Button>
            <Button
              variant="ghost"
              disabled={isLoadingCalendars}
              onClick={refreshCalendarConnections}
            >
              {isLoadingCalendars ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>

          {calendarConnections.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No calendar connected yet. Connect a calendar to enable meeting scheduling.
            </p>
          ) : (
            <div className="space-y-2">
              {calendarConnections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border p-3"
                >
                  <div className="space-y-1">
                    <div className="font-medium capitalize">
                      {connection.provider}
                      {connection.is_default && (
                        <Badge className="ml-2" variant="secondary">
                          Default
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {connection.account_email || 'Unknown account'} · {connection.status}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {!connection.is_default && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={calendarBusyProvider !== null}
                        onClick={() => setDefaultProvider(connection.provider)}
                      >
                        Make Default
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      disabled={calendarBusyProvider !== null}
                      onClick={() =>
                        disconnectCalendar(connection.id, connection.provider)
                      }
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {defaultCalendarProvider && (
            <p className="text-xs text-muted-foreground">
              Default provider: <span className="font-medium capitalize">{defaultCalendarProvider}</span>
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Email Connections
          </CardTitle>
          <CardDescription>
            Connect your email account to send outreach from your own inbox. Emails will appear in
            your Sent folder and recipients will see your real email address.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              disabled={emailBusyProvider === 'google'}
              onClick={() => connectEmail('google')}
            >
              {emailBusyProvider === 'google' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Connect Gmail
            </Button>
            <Button
              variant="outline"
              disabled={emailBusyProvider === 'microsoft'}
              onClick={() => connectEmail('microsoft')}
            >
              {emailBusyProvider === 'microsoft' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Connect Microsoft
            </Button>

            <Dialog open={smtpDialogOpen} onOpenChange={setSmtpDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Server className="mr-2 h-4 w-4" />
                  Connect SMTP
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Connect SMTP Server</DialogTitle>
                  <DialogDescription>
                    Connect your custom email server for sending outreach
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp-email">Email Address</Label>
                    <Input
                      id="smtp-email"
                      type="email"
                      placeholder="you@company.com"
                      value={smtpForm.email_address}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, email_address: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-name">Display Name (optional)</Label>
                    <Input
                      id="smtp-name"
                      placeholder="Your Name"
                      value={smtpForm.display_name || ''}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, display_name: e.target.value })
                      }
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="smtp-host">SMTP Host</Label>
                      <Input
                        id="smtp-host"
                        placeholder="smtp.company.com"
                        value={smtpForm.smtp_host}
                        onChange={(e) =>
                          setSmtpForm({ ...smtpForm, smtp_host: e.target.value })
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="smtp-port">Port</Label>
                      <Input
                        id="smtp-port"
                        type="number"
                        placeholder="587"
                        value={smtpForm.smtp_port}
                        onChange={(e) =>
                          setSmtpForm({ ...smtpForm, smtp_port: parseInt(e.target.value) || 587 })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-username">Username</Label>
                    <Input
                      id="smtp-username"
                      placeholder="username or email"
                      value={smtpForm.smtp_username}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, smtp_username: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp-password">Password</Label>
                    <Input
                      id="smtp-password"
                      type="password"
                      placeholder="App password or SMTP password"
                      value={smtpForm.smtp_password}
                      onChange={(e) =>
                        setSmtpForm({ ...smtpForm, smtp_password: e.target.value })
                      }
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="smtp-tls"
                      checked={smtpForm.smtp_use_tls}
                      onCheckedChange={(checked) =>
                        setSmtpForm({ ...smtpForm, smtp_use_tls: checked })
                      }
                    />
                    <Label htmlFor="smtp-tls">Use TLS</Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setSmtpDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={saveSMTPConnection} disabled={isSavingSMTP}>
                    {isSavingSMTP ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Connection'
                    )}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button
              variant="ghost"
              disabled={isLoadingEmails}
              onClick={refreshEmailConnections}
            >
              {isLoadingEmails ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>

          {emailConnections.length === 0 ? (
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                No email account connected. Connect your Gmail, Microsoft, or SMTP account to send
                outreach emails from your own inbox.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {emailConnections.map((connection) => (
                <div
                  key={connection.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border p-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 font-medium">
                      {getProviderLabel(connection.provider)}
                      {connection.is_default && (
                        <Badge variant="secondary">
                          <Star className="mr-1 h-3 w-3" />
                          Default
                        </Badge>
                      )}
                      {getHealthBadge(connection.health_status)}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {connection.email_address}
                      {connection.daily_send_count > 0 && (
                        <> · {connection.daily_send_count} emails sent today</>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={testingConnectionId === connection.id}
                      onClick={() => testEmailConnection(connection.id)}
                    >
                      {testingConnectionId === connection.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="h-4 w-4" />
                      )}
                    </Button>
                    {!connection.is_default && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDefaultEmailConnection(connection.id)}
                      >
                        Make Default
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      onClick={() => disconnectEmail(connection.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Outreach emails are sent from your connected email account. This improves deliverability
            and ensures replies come back to your inbox.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
